<template>
    <nav v-if="isNotPolicy"
         class="nav">
        <ul class="nav__master">
            <li>
                <nav-destination v-if="showBackButton"
                                 class="nav__master__back"
                                 :type="schemeBackHeaderStyle"
                                 :to="backButtonTarget"
                                 icon-class="fas fa-chevron-circle-left"
                                 icon-location="pre"
                                 :text="commonResources.back"
                                 @click="onBackClick" />
            </li>
            <li v-if="!showNothing"
                class="nav__master__destination nav__master__destination--user">
                <template v-if="!userSignedIn">
                    <template v-if="showLogInOnly">
                        <nav-destination v-if="stateIsClean"
                                         :to="{ name: 'LogIn' }"
                                         icon-class="fas fa-sign-in-alt"
                                         :text="commonResources.login" />
                        <nav-destination v-else
                                         ref="logInFromRegister"
                                         @click="onLogInFromRegisterClick"
                                         icon-class="fas fa-sign-in-alt"
                                         :text="commonResources.login" />
                    </template>
                    <nav-destination v-else-if="showRegisterOnly"
                                     :to="{ name: 'Register' }"
                                     icon-class="fas fa-user-plus"
                                     :text="commonResources.register" />
                    <nav-destination v-else
                                     ref="logInOrRegister"
                                     @click="onLogInOrRegisterClick"
                                     icon-class="fas fa-sign-in-alt"
                                     :text="`${ commonResources.login } / ${ commonResources.register }`" />
                </template>
                <nav-destination v-else
                                 :to="{ name: 'Dashboard' }"
                                 class="nav__master__destination--dashboard"
                                 text="Dashboard" />
            </li>
            <li v-if="subnavItems.length"
                class="nav__master__destination nav__master__destination--other">
                <subnav :items="subnavItems" />
            </li>
        </ul>
    </nav>
</template>

<script>

import Vuex from 'vuex'

import ProgressManager from '@/progress-manager'
import LogInConfirmation from '@/components/auth/modals/log-in-confirmation'
import LogInOrRegisterChooser from '@/components/auth/modals/log-in-or-register-chooser'
import NavDestination from './nav-destination'
import Subnav from './subnav'
import SubnavItems from '@/components/subnav-items'
import ResourcesHelper from '@/components/resources-helper'

export default {
    data() {
        return {
            /**
             * @type {boolean}
             */
            modalIsOpen: false,
        }
    },

    created() {
        this.$eventbus.$on(this.$appevents.showModal, () => this.modalIsOpen = true)
        this.$eventbus.$on(this.$appevents.hideModal, () => this.modalIsOpen = false)
    },

    beforeDestroy() {
        this.$eventbus.$off(this.$appevents.showModal)
        this.$eventbus.$off(this.$appevents.hideModal)
    },

    computed: {

        ...Vuex.mapGetters([
            'userSignedIn',
        ]),

        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {Project}
             */
            currentProject: stateStore => stateStore.improvements.currentProject,

            /**
             * @param {VuexState} stateStore
             * @returns {?PropertyAsJson}
             */
            property: stateStore => stateStore.property,

            /**
             * @param {VuexState} stateStore
             * @returns {boolean}
             */
            serviceDown: stateStore => stateStore.serviceDown,
        }),

        /**
         * @returns {boolean}
         */
        isLoginPage() {
            return [ 'LogIn', 'LogInWithContext' ].includes(this.$route.name)
        },

        /**
         * @returns {boolean}
         */
        isNotPolicy() {
            return !this.$route || !this.$route.path || !this.$route.path.includes('/policies/')
        },

        /**
         * @returns {string}
         */
        schemeBackHeaderStyle() {
            return this.$scheme.backHeaderButtonStyle
        },

        /**
         * @returns {boolean}
         */
        showLogInOnly() {
            return this.$route.name === 'Register' || !this.isLoginPage && !this.property
        },

        /**
         * @returns {boolean}
         */
        showRegisterOnly() {
            return this.isLoginPage && this.property
        },

        /**
         * @returns {boolean}
         */
        showNothing() {
            return this.serviceDown || !this.userSignedIn && (this.isLoginPage && !this.property)
        },

        /**
         * @returns {boolean}
         */
        showBackButton() {
            const exclude = [ 'Register', 'Welcome', 'GoAhead', 'Dashboard', 'MyAccount', 'Renovation' ]

            return !this.isLoginPage && !exclude.includes(this.$route.name)
        },

        /**
         * Return the name of the route for the back button.
         *
         * @returns {?{name: string, query: ?Object|undefined }} The to object.
         */
        backButtonTarget() {
            if (!this.showBackButton) return null
            if (this.modalIsOpen) return null

            return ProgressManager.getPrevious(this.$route.name)
        },

        /**
         * @returns {boolean}
         */
        stateIsClean() {
            return !this.currentProject || !this.currentProject.initialised
        },
    },

    methods: {
        /**
         *  Close an open modal on back button click.
         */
        onBackClick() {
            if (this.modalIsOpen)
                this.$eventbus.$emit(this.$appevents.hideModal)
        },

        /**
         * Event method.
         */
        onLogInFromRegisterClick() {
            this.$eventbus.$off(this.$appevents.afterModalClose)
            this.$eventbus.$on(this.$appevents.afterModalClose, () => {
                this.$nextTick(() => {
                    this.$logger.logObject({ ref: this.$refs.logInFromRegister }, 'Post close action', 'Modal')
                    this.$refs.logInFromRegister.$el.focus()
                })
            })
            history.pushState({ key: 'log-in-confirm' }, 'Log in confirmation', '#')
            this.$eventbus.$emit(this.$appevents.showModal, LogInConfirmation)
        },

        /**
         * Event method.
         */
        onLogInOrRegisterClick() {
            this.$eventbus.$off(this.$appevents.afterModalClose)
            this.$eventbus.$on(this.$appevents.afterModalClose, () => {
                this.$nextTick(() => {
                    this.$logger.logObject({ ref: this.$refs.logInOrRegister }, 'Post close action', 'Modal')
                    this.$refs.logInOrRegister.$el.focus()
                })
            })
            history.pushState({ key: 'log-in-or-register' }, 'Log in or Register', '#')
            this.$eventbus.$emit(this.$appevents.showModal, LogInOrRegisterChooser)
        },
    },

    components: {
        NavDestination,
        Subnav,
    },

    mixins: [
        ResourcesHelper,
        SubnavItems,
    ],
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\header\_main-nav.scss

.nav {

    @include element('master') {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-flow: nowrap;
        justify-content: flex-end;
        align-items: flex-start;

        @include bp-min-small {
            position: relative;
        }

        @include element('destination') {
            padding: 0 0.6rem;
            @include user-select-none;

            &:first-child {
                padding-left: 0;
            }

            &:last-child {
                padding-right: 0;
            }

            @include modifier('user') {
                display: none;

                @include bp-min-small {
                    display: block;
                    padding-right: 0;
                }
            }

            @include modifier('other') {
                display: block;

                @include bp-min-small {
                    display: none;
                }
            }

            @include modifier('dashboard') {
                margin-right: 0.6rem !important;

                @include bp-min-small {
                  margin-right: 0 !important;
                }
            }
        }
    }
}

</style>
